import "@/init/chartjs"
import "@/common/fields/components" // registers the frontend's field renderers
import { createApp, h, provide } from "vue"
import { RouterLink } from "vue-router"
import { DefaultApolloClient } from "@vue/apollo-composable"
import { createPinia } from "pinia"
import { FontAwesomeIcon } from "@auger/ui"
import { registerAugerLinks } from "@auger/ui/components/link/registry"
import { registerViewSettingsAdapter } from "@auger/ui/composables/viewSettings/adapter"
import { clickOutside } from "@auger/ui/directives/clickOutside"
import { tooltip } from "@auger/ui/directives/tooltip"
import { getRecordUrl } from "@/common/records"
import RecordLink from "@/components/records/RecordLink.vue"
import auth from "@/identity/auth"
import { client } from "@/init/apollo"
import { posthogEnabled } from "@/init/posthog"
import { installIdentitySessionRecordingGuard } from "@/init/posthogSessionRecording"
import { createUserViewSettingsAdapter } from "@/init/viewSettings"
import App from "./App.vue"
import router from "./router"

// Views save their settings (grid columns, layouts, ...) to the user's own data.
registerViewSettingsAdapter(createUserViewSettingsAdapter())

// The package's chips link to a record or a tag; the router decides where that goes.
registerAugerLinks({
  component: RouterLink,
  resolve: (target) =>
    target.kind === "tag" ? `/tags/${target.slug || "error"}` : getRecordUrl(target),
})

const pinia = createPinia()
const app = createApp({
  setup: () => provide(DefaultApolloClient, client),
  render: () => h(App),
})

app.use(pinia)
app.use(router)
// Layer 1 (router half) of the session-replay defense in posthog.ts /
// posthogSessionRecording.ts: stops recording on the way into an identity
// route, restarts it on the way out, for in-app navigation. `posthogEnabled`
// gates it off entirely in dev / no-API-key builds — see
// installIdentitySessionRecordingGuard's own comment for why that matters.
installIdentitySessionRecordingGuard(router, posthogEnabled)

// enabling of devtools via config.devtools has been removed, we can now use the
// __VUE_PROD_DEVTOOLS__ compile-time flag - details:
// https://github.com/vuejs/core/tree/main/packages/vue#bundler-build-feature-flags

// make auth service globally available - maybe replace this with provide/inject later
app.config.globalProperties.$auth = auth

// global components
app.component("FontAwesomeIcon", FontAwesomeIcon)
app.component("RecordLink", RecordLink)

// global custom directives
app.directive("click-outside", clickOutside)
app.directive("tooltip", tooltip)

// app.config.errorHandler below only sees SYNCHRONOUS throws out of a component.
// The write paths (records.update, useList/useRecords updateItem, useRecord's
// relation reconcile) are async by design — a guard's refusal has to reach a
// caller's .catch() rather than skip it — which means a caller that discards the
// promise loses the failure entirely. Every such caller is fixed and the write
// arbiters toast their own refusals, so this is a net, not the mechanism: it
// makes the next discarded rejection loud in the console instead of invisible.
window.addEventListener("unhandledrejection", (event) => {
  console.group("🔴 Unhandled Promise Rejection")
  console.error("Reason:", event.reason)
  if (event.reason?.stack) console.error("Stack trace:", event.reason.stack)
  console.groupEnd()
})

// Global error handler to show actual error details
app.config.errorHandler = (thrown, instance, info) => {
  const err = thrown as {
    message?: unknown
    stack?: unknown
    constructor?: { name?: string }
  } | null
  console.group("🔴 Vue Error Handler")

  // Basic error info
  console.error("Error:", err)
  console.error("Error message:", err?.message)
  console.error("Error type:", err?.constructor?.name)
  console.error("Error info:", info)

  // Component info
  const componentName =
    instance?.$options?.name || instance?.$options?.__name || instance?.$?.type?.name
  const componentFile = instance?.$?.type?.__file
  console.error("Component name:", componentName || "undefined")
  if (componentFile) console.error("Component file:", componentFile)

  // Route info
  const route = instance?.$route || instance?.$.appContext?.config?.globalProperties?.$route
  if (route) {
    console.error("Current route:", route.path)
    console.error("Route name:", route.name)
  }

  // Stack trace
  if (err?.stack) {
    console.error("Stack trace:", err.stack)
  }

  console.groupEnd()
}

app.mount("#app")
